import { convertBlobToJpeg } from 'utils/ui/convertBlobToJpeg';

/**
 * Trigger a browser download for a given Blob using the anchor click pattern.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Download a single image by fetching it as a blob and triggering a download.
 * Converts the fetched blob to JPEG using the Canvas API before downloading.
 * Uses fetch + createObjectURL to handle cross-origin images reliably
 * (native `<a download>` is silently ignored for cross-origin URLs).
 */
export async function downloadImage(url: string, title: string, carName?: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
  }
  const rawBlob = await response.blob();
  const blob = await convertBlobToJpeg(rawBlob);

  const baseName = title || 'image';
  const filename = carName ? `${carName} - ${baseName}.jpg` : `${baseName}.jpg`;

  downloadBlob(blob, filename);
}

/**
 * Build a comparison snapshot filename: "{car1}_vs_{car2}_{angle}.png".
 * Falls back gracefully if either car name or angle is missing.
 * PNG (not JPEG) because the snapshot preserves transparent backgrounds from
 * the source car renders so dealers get a clean product cutout.
 */
export function buildComparisonFilename(
  car1Name: string | undefined,
  car2Name: string | undefined,
  angle: string | undefined
): string {
  const parts: string[] = [];
  if (car1Name) parts.push(car1Name);
  if (car2Name) parts.push(car2Name);

  const carsSegment = parts.length > 0 ? parts.join('_vs_') : 'comparison';
  const angleSegment = angle ? `_${angle}` : '';

  return `${carsSegment}${angleSegment}.png`;
}
